<div class="flex h-full min-h-0 w-full flex-col overflow-y-auto scrollbar-transparent">
  <div class="shrink-0 space-y-4 p-4">
    @if (!isEmbedded()) {
    <div>
      <h4 class="text-sm font-semibold text-primary">Super Socials</h4>
      <p class="mt-1 text-xs text-secondary m-0">
        Look up a creator in SuperSocial by Shout Firebase UID. View connected accounts, create
        missing links, refresh orchestrator data, or remove an account so a new one can be connected.
        GodMode only.
      </p>
    </div>

    <div class="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
      @if (!settingsContext.hasCreatorId()) {
      <p class="sm:col-span-2 text-sm text-amber-800 dark:text-amber-200 m-0" role="status">
        Enter creator Firebase UID in the context bar above, then look up the creator.
      </p>
      }

      <div class="sm:col-span-2">
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          (click)="runLookup()"
          [disabled]="searchLoading() || !settingsContext.hasCreatorId()"
        >
          @if (searchLoading()) {
            Looking up…
          } @else {
            Look up creator
          }
        </button>
      </div>
    </div>
    } @else if (searchLoading()) {
      <p class="text-sm text-secondary m-0">Loading SuperSocial profile…</p>
    }

    @if (searchError()) {
    <p class="text-sm text-red-600 dark:text-red-400 m-0" role="alert">{{ searchError() }}</p>
    }
  </div>

  @if (superSocialProfile(); as profile) {
  <div class="shrink-0 space-y-4 border-t border-zinc-200 p-4 dark:border-zinc-800">
    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Accounts</h5>
      <p class="mt-0.5 text-xs text-secondary">
        Creator accounts in SuperSocial (lookup by Shout creator UID / externalId).
      </p>

      <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt class="font-medium text-secondary">businessId</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ businessId }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">externalId (from search)</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ resolvedCreatorId() || '—' }}</dd>
        </div>
      </dl>

      @if (!profile.found) {
      <div class="mt-4 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
        <p class="m-0 text-sm text-amber-800 dark:text-amber-200">
          No SuperSocial creator found for this Shout ID and business. Create one to link social
          accounts.
        </p>
        <div class="space-y-1">
          <label for="super-socials-create-name" class="block text-sm font-medium text-primary"
            >Display name (optional)</label
          >
          <input
            id="super-socials-create-name"
            type="text"
            autocomplete="off"
            class="block w-full max-w-md rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="createCreatorName()"
            (input)="onCreateCreatorNameInput($any($event.target).value)"
            [disabled]="createCreatorLoading()"
            placeholder="Defaults to externalId if empty"
          />
        </div>
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          (click)="createCreatorRecord()"
          [disabled]="createCreatorLoading() || !resolvedCreatorId()"
        >
          @if (createCreatorLoading()) {
            Creating…
          } @else {
            Create SuperSocial creator
          }
        </button>
      </div>
      } @else {
      <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt class="font-medium text-secondary">SuperSocial creator ID</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ profile.superSocialCreatorId ?? '—' }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">externalId (Shout UID)</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ profile.externalId ?? '—' }}</dd>
        </div>
      </dl>

      @if (profile.accounts.length) {
      <ul class="mt-4 space-y-4">
        @for (account of profile.accounts; track account.id) {
        <li class="rounded-lg border border-zinc-100 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
          <div class="flex flex-wrap items-start gap-4">
            @if (account.avatarUrl) {
            <img
              [src]="account.avatarUrl"
              [alt]="account.platformLabel + ' profile'"
              class="h-16 w-16 shrink-0 rounded-full border border-zinc-200 object-cover dark:border-zinc-700"
              width="64"
              height="64"
            />
            }

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold text-primary">{{ account.platformLabel }}</p>
                  <div class="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      autocomplete="off"
                      spellcheck="false"
                      class="block w-[300px] max-w-full rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                      [value]="getHandleDraft(account.id)"
                      (input)="onHandleDraftInput(account.id, $any($event.target).value)"
                      [disabled]="isHandleUpdateLoading(account.id)"
                      [attr.aria-label]="account.platformLabel + ' handle'"
                    />
                    @if (isHandleDraftDirty(account)) {
                    <button
                      type="button"
                      class="btn-shout btn-shout-xs btn-shout-primary shrink-0"
                      (click)="updateAccountHandle(account)"
                      [disabled]="isHandleUpdateLoading(account.id)"
                    >
                      @if (isHandleUpdateLoading(account.id)) {
                        Updating…
                      } @else {
                        Update handle
                      }
                    </button>
                    }
                  </div>
                </div>

                @if (supportsSuperSocialAccountRefresh(account.platform)) {
                <div class="flex shrink-0 flex-wrap items-center gap-2">
                  @if (isSuperSocialRefreshLoading(account.id)) {
                  <div class="flex shrink-0 flex-wrap items-center gap-2">
                    <span
                      class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary inset-ring inset-ring-zinc-200 dark:inset-ring-zinc-700"
                    >
                      <svg
                        aria-hidden="true"
                        class="size-4 animate-spin text-secondary"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                      Syncing account data…
                    </span>
                    <button
                      type="button"
                      class="rounded-md px-2 py-1 text-xs font-medium text-secondary inset-ring inset-ring-zinc-200 hover:bg-zinc-100 dark:inset-ring-zinc-700 dark:hover:bg-zinc-800"
                      (click)="cancelRefreshForAccount(account.id)"
                    >
                      Cancel
                    </button>
                  </div>
                  } @else if (isSuperSocialRefreshSent(account.id)) {
                  <span
                    class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 inset-ring inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20"
                    >Refresh request sent!</span
                  >
                  } @else {
                  <button
                    type="button"
                    class="btn-shout btn-shout-xs btn-shout-primary inline-flex items-center gap-x-1.5"
                    (click)="refreshSuperSocialAccount(account)"
                  >
                    Refresh Account
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="-mr-0.5 size-5"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                      <path d="M16 16h5v5" />
                    </svg>
                  </button>
                  }
                  <button
                    type="button"
                    class="rounded-md px-2 py-1 text-xs font-medium text-red-700 inset-ring inset-ring-red-600/20 hover:bg-red-50 dark:text-red-400 dark:inset-ring-red-500/30 dark:hover:bg-red-950/40 disabled:opacity-50"
                    (click)="removeAccount(account)"
                    [disabled]="isRemoveAccountLoading(account.id)"
                  >
                    @if (isRemoveAccountLoading(account.id)) {
                      Removing…
                    } @else {
                      Remove account
                    }
                  </button>
                </div>
                } @else {
                <div class="flex shrink-0 items-center">
                  <button
                    type="button"
                    class="rounded-md px-2 py-1 text-xs font-medium text-red-700 inset-ring inset-ring-red-600/20 hover:bg-red-50 dark:text-red-400 dark:inset-ring-red-500/30 dark:hover:bg-red-950/40 disabled:opacity-50"
                    (click)="removeAccount(account)"
                    [disabled]="isRemoveAccountLoading(account.id)"
                  >
                    @if (isRemoveAccountLoading(account.id)) {
                      Removing…
                    } @else {
                      Remove account
                    }
                  </button>
                </div>
                }
              </div>

              <dl class="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                <div>
                  <dt class="inline font-medium text-secondary">id: </dt>
                  <dd class="inline font-mono text-xs break-all text-primary">{{ account.id }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">source: </dt>
                  <dd class="inline text-primary">{{ account.source ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">createdDate: </dt>
                  <dd class="inline text-primary">{{ account.createdDate ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">updatedDate: </dt>
                  <dd class="inline" [class]="superSocialUpdatedDateColour(account.updatedDateIso)">
                    {{ account.updatedDate ?? '—' }}
                  </dd>
                </div>

                @if (account.platform === 'instagram') {
                <div>
                  <dt class="inline font-medium text-secondary">name: </dt>
                  <dd class="inline text-primary">{{ account.name ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">followers_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.followers_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">follows_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.follows_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">media_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.media_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalViews: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalViews) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalLikes: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalLikes) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalComments: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalComments) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalShares: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalShares) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalSaves: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalSaves) }}</dd>
                </div>
                }

                @if (account.platform === 'tiktok') {
                <div>
                  <dt class="inline font-medium text-secondary">displayName: </dt>
                  <dd class="inline text-primary">{{ account.displayName ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">followers_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.followers_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">follows_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.follows_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">media_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.media_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalViews: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalViews) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalLikes: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalLikes) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalComments: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalComments) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalShares: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalShares) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">totalSaves: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.totalSaves) }}</dd>
                </div>
                }

                @if (account.platform === 'youtube') {
                <div>
                  <dt class="inline font-medium text-secondary">title: </dt>
                  <dd class="inline text-primary">{{ account.title ?? '—' }}</dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="inline font-medium text-secondary">channel_id: </dt>
                  <dd class="inline font-mono text-xs break-all text-primary">{{ account.channel_id ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">subscriber_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.subscriber_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">view_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.view_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">video_count: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.video_count) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">total_likes: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.total_likes) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-secondary">total_comments: </dt>
                  <dd class="inline text-primary">{{ formatNumber(account.total_comments) }}</dd>
                </div>
                }
              </dl>
            </div>
          </div>
        </li>
        }
      </ul>
      } @else {
      <p class="mt-2 text-sm text-secondary">
        SuperSocial creator exists but has no connected Instagram, TikTok, or YouTube accounts.
      </p>
      <p class="mt-2 text-xs text-secondary">
        Accounts are scoped to this businessId. If the handle already exists on another SuperSocial
        business, Create will fail instead of linking it here.
      </p>
      }

      <div class="mt-6 space-y-4">
        <h6 class="text-sm font-semibold text-primary">Connect missing platforms</h6>

        @if (!hasTiktok()) {
        <div class="rounded-lg border border-zinc-100 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
          <p class="m-0 text-sm font-semibold text-primary">TikTok (TikAPI)</p>
          <p class="mt-0.5 text-xs text-secondary">
            Calls <code class="text-xs">POST /tiktok-account/create-tikapi</code>. externalId and
            businessId are taken from the lookup above.
          </p>
          <div class="mt-3 flex flex-wrap items-end gap-2">
            <div class="min-w-[200px] flex-1 space-y-1">
              <label for="super-socials-tt-handle" class="block text-sm font-medium text-primary"
                >Handle</label
              >
              <input
                id="super-socials-tt-handle"
                type="text"
                autocomplete="off"
                spellcheck="false"
                class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [value]="createTiktokHandle()"
                (input)="onCreateTiktokHandleInput($any($event.target).value)"
                [disabled]="isCreatePlatformLoading('tiktok')"
                placeholder="marketwith.el"
              />
            </div>
            <div class="flex items-center justify-end w-36">
              <button
              type="button"
              class="btn-basic-sm btn-primary shrink-0 h-[32px]"
              (click)="createTiktokAccount()"
              [disabled]="isCreatePlatformLoading('tiktok') || !createTiktokHandle().trim()"
            >
              @if (isCreatePlatformLoading('tiktok')) {
                Creating…
              } @else {
                Create TikTok
              }
            </button>
            </div>
          </div>
        </div>
        }

        @if (!hasInstagram()) {
        <div class="rounded-lg border border-zinc-100 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
          <p class="m-0 text-sm font-semibold text-primary">Instagram (Apify)</p>
          <p class="mt-0.5 text-xs text-secondary">
            Calls <code class="text-xs">POST /instagram-account/create-apify</code>.
          </p>
          <div class="mt-3 flex flex-wrap items-end gap-2">
            <div class="min-w-[200px] flex-1 space-y-1">
              <label for="super-socials-ig-handle" class="block text-sm font-medium text-primary"
                >Handle</label
              >
              <input
                id="super-socials-ig-handle"
                type="text"
                autocomplete="off"
                spellcheck="false"
                class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [value]="createInstagramHandle()"
                (input)="onCreateInstagramHandleInput($any($event.target).value)"
                [disabled]="isCreatePlatformLoading('instagram')"
                placeholder="username"
              />
            </div>
            <div class="flex items-center justify-end w-36">
            <button
              type="button"
              class="btn-basic-sm btn-primary shrink-0 h-[32px]"
              (click)="createInstagramAccount()"
              [disabled]="isCreatePlatformLoading('instagram') || !createInstagramHandle().trim()"
            >
              @if (isCreatePlatformLoading('instagram')) {
                Creating…
              } @else {
                Create Instagram
              }
            </button>
            </div>
          </div>
        </div>
        }

        @if (!hasYoutube()) {
        <div class="rounded-lg border border-zinc-100 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
          <p class="m-0 text-sm font-semibold text-primary">YouTube</p>
          <p class="mt-0.5 text-xs text-secondary">
            Calls <code class="text-xs">POST /youtube-account/create-youtube</code>.
          </p>
          <div class="mt-3 flex flex-wrap items-end gap-2">
            <div class="min-w-[200px] flex-1 space-y-1">
              <label for="super-socials-yt-channel" class="block text-sm font-medium text-primary"
                >Channel ID or handle</label
              >
              <input
                id="super-socials-yt-channel"
                type="text"
                autocomplete="off"
                spellcheck="false"
                class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [value]="createYoutubeChannel()"
                (input)="onCreateYoutubeChannelInput($any($event.target).value)"
                [disabled]="isCreatePlatformLoading('youtube')"
                placeholder="@handle or UC…"
              />
            </div>
            <div class="flex items-center justify-end w-36">
            <button
              type="button"
              class="btn-basic-sm btn-primary shrink-0 h-[32px]"
              (click)="createYoutubeAccount()"
              [disabled]="isCreatePlatformLoading('youtube') || !createYoutubeChannel().trim()"
            >
              @if (isCreatePlatformLoading('youtube')) {
                Creating…
              } @else {
                Create YouTube
              }
            </button>
            </div>
          </div>
        </div>
        }

        @if (hasTiktok() && hasInstagram() && hasYoutube()) {
        <p class="m-0 text-sm text-secondary">All three platforms are already connected.</p>
        }
      </div>
      }
    </section>
  </div>
  }
</div>
